import type { ActiveDocumentInfo } from '../../../shared/hooks/useActiveDocumentName';
import { SettingsType } from '../../../shared/api/pipeex/pcfSettingsClient';
import { useConfigurationStore } from '../../../shared/stores/useConfigurationStore';
import type { ScopeEditorPersistenceState } from '../lib/scopeEditorPersistence';
import { isFlowSnapshotDefaultProfileName } from '../lib/profile';
import type { useMappingConfigurationContext } from '../ui/MappingConfigurationContext';
import type { MappingConfigurationPageContentProps } from './mappingWorkspaceContentModel.types';
import { useGuardedProfileActions } from './useGuardedProfileActions';
import { useMappingReadinessNotifications } from './useMappingReadinessNotifications';
import type { MappingWorkspaceFocusRefs } from './useMappingWorkspaceFocusRefs';
import { useProfileLoadConfirmation } from './useProfileLoadConfirmation';
import { useProjectProfileGate } from './useProjectProfileGate';
import { useProjectProfileVerifiedBanner } from './useProjectProfileVerifiedBanner';
import { useRepairConsentGate } from './useRepairConsentGate';
import { useSetupReadinessCheckpoint } from './useSetupReadinessCheckpoint';
import { useWorkspaceProgressIndicators } from './useWorkspaceProgressIndicators';
import { isMappingProfileApplyInFlight } from './profileControllerShared';

type ConfigurationState = ReturnType<typeof useConfigurationStore.getState>;
type MappingConfigurationContext = ReturnType<typeof useMappingConfigurationContext>;

interface UseMappingWorkspaceProfileRuntimeArgs {
  props: MappingConfigurationPageContentProps;
  activeDocumentInfo: ActiveDocumentInfo;
  objectTypes: MappingConfigurationContext['objectTypeOptions'];
  menuConfig: ConfigurationState['menuConfiguration'];
  connectorConfig: ConfigurationState['connectorBindingConfiguration'];
  processConfig: ConfigurationState['processPropertiesConfiguration'];
  boltConfig: ConfigurationState['boltConfiguration'];
  categoryConfig: ConfigurationState['additionalCategoriesConfiguration'];
  processConditionsEditorState: ScopeEditorPersistenceState;
  projectPropertiesEditorState: ScopeEditorPersistenceState;
  focusRefs: MappingWorkspaceFocusRefs;
  showMappingInfo: (message: string, duration?: number) => void;
}

export const useMappingWorkspaceProfileRuntime = ({
  props,
  activeDocumentInfo,
  objectTypes,
  menuConfig,
  connectorConfig,
  processConfig,
  boltConfig,
  categoryConfig,
  processConditionsEditorState,
  projectPropertiesEditorState,
  focusRefs,
  showMappingInfo,
}: UseMappingWorkspaceProfileRuntimeArgs) => {
  const {
    activeUserId,
    selectedProfileKey,
    selectedSettingsType,
    lastAppliedProfileKey,
    profileApplyProgress,
    profiles,
    profilesLoading,
    projectPreferredProfile,
    projectIdentityPending,
    dockablePaneVisible,
    isLiveWizardMappingHandoff,
    skipSetupReadiness,
    profileTrustRevision,
    isSaving,
    onSelectedProfileChange,
    onLoadProfile,
    clearProfileApplyProgress,
  } = props;

  const gate = useProjectProfileGate({
    activeUserId,
    activeDocumentInfo,
    selectedProfileKey,
    selectedSettingsType,
    lastAppliedProfileKey,
    profileApplyProgress,
    profiles,
    profilesLoading,
    projectPreferredProfile,
    projectIdentityPending,
    dockablePaneVisible,
    isLiveWizardMappingHandoff,
    profileTrustRevision,
    isSaving,
    onSelectedProfileChange,
    onLoadProfile,
  });

  const hasResolvedSelectedProfile = selectedProfileKey.trim() !== '';
  const isBundledDefaultUserProfile =
    isFlowSnapshotDefaultProfileName(gate.resolvedProfileName) &&
    gate.resolvedSettingsType === SettingsType.User;

  const repair = useRepairConsentGate({
    activeUserId,
    activeProjectSessionKey: gate.activeProjectSessionKey,
    resolvedProfileKey: gate.resolvedProfileKey,
    resolvedProfileName: gate.resolvedProfileName,
    profileConfirmedForCurrentSession: gate.profileConfirmedForCurrentSession,
    projectProfileTrust: gate.projectProfileTrust,
    projectProfileGateProfileKey: gate.projectProfileGateProfileKey,
    profileProjectMatches: gate.profileProjectMatches,
    handleProjectProfileGateConfirm: gate.handleProjectProfileGateConfirm,
    profileApplySessionActive: isMappingProfileApplyInFlight(profileApplyProgress),
    isLiveWizardMappingHandoff,
    skipSetupReadiness,
    dockablePaneVisible,
    projectIdentityPending,
    hasResolvedSelectedProfile,
    objectTypes,
    menuConfig,
    connectorConfig,
    boltConfig,
    processConfig,
    additionalCategoriesConfig: categoryConfig,
    processConditionsEditorState,
    projectPropertiesEditorState,
  });

  useSetupReadinessCheckpoint(repair.mappingReadiness.status);

  const profileLoad = useProfileLoadConfirmation({
    profileProjectMatches: gate.profileProjectMatches,
    activeDocumentInfo,
    lastAppliedProfileKey,
    selectedProfileKey,
    onProfileLoadConfirmed: repair.grantRepairConsent,
  });

  const guarded = useGuardedProfileActions({
    selectedProfileKey,
    lastAppliedProfileKey,
    applySelectedProfileChange: gate.handleWorkspaceSelectedProfileChange,
    loadProfileForCurrentProject: gate.handleLoadProfileForCurrentProject,
    confirmProfileLoad: profileLoad.confirmLoadSelectedProfile,
  });

  const showProjectProfileVerifiedBanner = useProjectProfileVerifiedBanner({
    profileConfirmedForCurrentSession: gate.profileConfirmedForCurrentSession,
    activeProjectSessionKey: gate.activeProjectSessionKey,
    readinessStatus: repair.mappingReadiness.status,
    isBundledDefaultUserProfile,
    selectedProfileKey,
    lastAppliedProfileKey,
  });

  const progress = useWorkspaceProgressIndicators({
    projectIdentityPending,
    profilesLoading,
    hasResolvedSelectedProfile,
    trustedProjectProfileAwaitingSessionLoad: gate.trustedProjectProfileAwaitingSessionLoad,
    resolvedProfileName: gate.resolvedProfileName,
    projectProfileChoiceBlocksQuickFill: gate.projectProfileChoiceBlocksQuickFill,
    profileProjectMatchesLoading: gate.profileProjectMatches.isLoading,
    activeProjectSessionKey: gate.activeProjectSessionKey,
    profileConfirmedForCurrentSession: gate.profileConfirmedForCurrentSession,
    projectProfileGateOpen: gate.projectProfileGateOpen,
    profileApplyProgress,
    mappingReadiness: repair.mappingReadiness,
    clearProfileApplyProgress,
  });

  useMappingReadinessNotifications({
    mappingReadiness: repair.mappingReadiness,
    activeDocumentInfo,
    activeProjectSessionKey: gate.activeProjectSessionKey,
    resolvedProfileKey: gate.resolvedProfileKey,
    focusRefs,
    showMappingInfo,
  });

  return {
    gate,
    repair,
    profileLoad,
    guarded,
    showProjectProfileVerifiedBanner,
    progress,
    hasResolvedSelectedProfile,
  };
};
